import type { FragmentData } from "@gqlb/core";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import NextLink from "next/link";
import { useEffect, useMemo, useRef } from "react";
import type { MealTagShowcase_Fragment } from "@/components/pages/home/MealTagShowcase";
import { type MEDIA_FRAGMENT, ResponsiveImage } from "@/components/ResponsiveImage";

gsap.registerPlugin(ScrollTrigger);

export const CategoryReel: React.FC<{
  data: FragmentData<typeof MealTagShowcase_Fragment>;
}> = ({ data }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  const previews = useMemo(() => {
    const items: Array<{
      id: string;
      name: string;
      image: FragmentData<typeof MEDIA_FRAGMENT> | undefined;
      link: string;
    }> = [];

    const tags = [
      {
        data: data.breakfast,
        name: data.breakfast?.name ?? "Breakfast",
        link: data.breakfast ? `/menu?tags=${data.breakfast.id}` : "/menu",
      },
      {
        data: data.lunch,
        name: "Lunch",
        link: data.lunch ? `/menu?tags=${data.lunch.id}` : "/menu",
      },
      {
        data: data.dinner,
        name: "Dinner",
        link: data.dinner ? `/menu?tags=${data.dinner.id}` : "/menu",
      },
      {
        data: data.dessert,
        name: data.dessert?.name ?? "Dessert",
        link: data.dessert ? `/menu?tags=${data.dessert.id}` : "/menu",
      },
    ];

    for (const tag of tags) {
      if (tag.data && tag.data.products.length > 0) {
        items.push({
          id: tag.data.id,
          name: tag.name,
          image: tag.data.products[0]?.images[0],
          link: tag.link,
        });
      }
    }
    return items;
  }, [data]);

  useEffect(() => {
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headingRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: headingRef.current,
            start: "top 85%",
          },
        },
      );

      const cards = cardsRef.current?.children;
      if (cards) {
        gsap.fromTo(
          cards,
          { y: 80, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            stagger: 0.15,
            ease: "power2.out",
            scrollTrigger: {
              trigger: cardsRef.current,
              start: "top 80%",
            },
          },
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-6 max-w-7xl mx-auto">
      <div ref={headingRef} className="text-center mb-16 opacity-0">
        <p className="text-brand-mint font-semibold tracking-widest uppercase text-sm mb-3">
          Our Menu
        </p>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
          Ready to Eat. Farm-to-Table. 100% Gluten-Free.
        </h2>
        <p className="mt-4 text-gray-500 text-lg max-w-xl mx-auto">
          From sunrise to sweet treats, we&apos;ve got you covered. Our chefs carefully create
          healthy, flavorful recipes to fuel your entire day.
        </p>
      </div>
      <div ref={cardsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {previews.map((item) => (
          <NextLink
            key={item.id}
            href={item.link}
            className="group relative rounded-2xl overflow-hidden aspect-[4/5] block"
          >
            {item.image && (
              <ResponsiveImage
                image={item.image}
                objectFit="cover"
                className="absolute inset-0 w-full h-full"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-[1] group-hover:from-black/80 transition-all duration-300" />
            <div className="absolute bottom-0 left-0 right-0 p-6 z-[2]">
              <h3 className="text-white text-xl md:text-2xl font-semibold">{item.name}</h3>
              <span className="text-brand-mint text-sm font-medium mt-1 inline-block group-hover:translate-x-1 transition-transform">
                Explore &rarr;
              </span>
            </div>
          </NextLink>
        ))}
      </div>
    </section>
  );
};
